/**
 * StorageService
 * F12 (PRD): Export/Import progress as JSON, plus a localStorage wrapper.
 * Referenced in PRD Appendix B as `src/services/StorageService.js`.
 */
export class StorageService {
  // ─── Export / Import ───────────────────────────────────────────────────────

  /**
   * Export a user's course progress as a downloadable JSON file.
   * @param {object} progressData  The progress object or snapshot to export.
   * @param {string} userId
   * @param {string} courseId
   */
  static exportProgress(progressData, userId, courseId) {
    const exportPayload = {
      exportedAt: new Date().toISOString(),
      version: '1.0',
      userId,
      courseId,
      progress: progressData,
    };

    const json = JSON.stringify(exportPayload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `progress-${courseId}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Open a file picker and call `onImport(error, data)` with the parsed JSON.
   * @param {function} onImport  Callback `(err, data) => void`
   */
  static importProgress(onImport) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';

    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          onImport(null, data);
        } catch {
          onImport(new Error('Invalid JSON file — could not parse progress data.'), null);
        }
      };
      reader.onerror = () => {
        onImport(new Error('Failed to read file.'), null);
      };
      reader.readAsText(file);
    };

    document.body.appendChild(input);
    input.click();
    // Remove after click so it doesn't linger in the DOM
    requestAnimationFrame(() => document.body.removeChild(input));
  }

  // ─── localStorage Wrapper ──────────────────────────────────────────────────

  /**
   * Retrieve and JSON-parse a value from localStorage.
   * @param {string} key
   * @returns {*} Parsed value, or null if missing / parse error.
   */
  static get(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * JSON-stringify and store a value in localStorage.
   * @param {string} key
   * @param {*} value
   * @returns {boolean} true on success, false if storage is unavailable.
   */
  static set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Remove a key from localStorage.
   * @param {string} key
   */
  static remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }

  /**
   * Clear all localStorage data (use with caution).
   */
  static clear() {
    try {
      localStorage.clear();
    } catch {
      // Ignore
    }
  }
}

export const storageService = StorageService;
